"use client";

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LuBanknote } from "react-icons/lu";
import { LuShoppingCart } from "react-icons/lu";
import { FiCreditCard } from "react-icons/fi";
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import Button from "../../../components/Button";
import styles from "./css/codPayment.module.css";
import { lookupReceipt as apiLookupReceipt } from "../../../api/frontShop/ReceiptAPI";
import { loadUnpaidOrderToCart as apiLoadUnpaidOrderToCart } from "../../../api/frontShop/CheckOutAPI";

export default function CodPayment() {
  const location = useLocation();
  const navigate = useNavigate();
  const [total, setTotal] = useState(0);
  const [amountGiven, setAmountGiven] = useState("");
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [paymentTitle, setPaymentTitle] = useState(
    location.state && typeof location.state.paymentTitle === "string"
      ? location.state.paymentTitle
      : "COD Payment"
  );

  const fromSplit = !!(location.state && location.state.splitFlow);
  const fromDebtorsSplit = !!(location.state && location.state.fromDebtors);
  const method1 = location.state && typeof location.state.method1 === "string" ? location.state.method1 : "cod";
  const recieptno = location.state && typeof location.state.recieptno === "string" ? location.state.recieptno : null;

  useEffect(() => {
    let cancelled = false;

    if (location.state && typeof location.state.total === "number") {
      setTotal(location.state.total);
      return () => {
        cancelled = true;
      };
    }

    const rec = location.state && typeof location.state.recieptno === "string" ? location.state.recieptno : "";
    if (rec) {
      (async () => {
        try {
          const { data: json } = await apiLookupReceipt(rec);
          const info = json?.data;
          const opt = Number(info?.optionamount);
          const tot = Number(info?.totalamount);
          const next = Number.isFinite(opt) && opt > 0 ? opt : Number.isFinite(tot) && tot > 0 ? tot : 0;
          if (!cancelled) setTotal(next);

          const optionIds = Array.isArray(info?.optionid) ? info.optionid.map(String) : [];
          if (!cancelled && optionIds.includes("COLL")) {
            setPaymentTitle("Collection Payment");
          }
          return;
        } catch {
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    try {
      const raw = localStorage.getItem("frontshop_basket");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const subtotal = parsed.reduce((sum, item) => sum + (Number(item.price) || 0) * (Number(item.qty) || 0), 0);
          const tax = subtotal * 0.15;
          setTotal(subtotal + tax);
        }
      }
    } catch {
    }
    return () => {
      cancelled = true;
    };
  }, [location.state]);

  useEffect(() => {
    if (!total) return;
    setAmountGiven((prev) => (prev === "" ? `R ${total.toFixed(2)}` : prev));
  }, [total]);

  const handleBack = () => {
    if (fromSplit) {
      navigate("/frontshop/payment-types/cod", {
        state: { total, splitFlow: true, method1, fromDebtors: fromDebtorsSplit },
      });
      return;
    }
    navigate("/frontshop");
  };

  const handleContinue = () => {
    if (!selectedMethod) return;
    if (selectedMethod === "cash") {
      if (fromSplit) {
        navigate("/frontshop/payment-types/cash", {
          state: { total, splitFlow: true, method1, fromDebtors: fromDebtorsSplit, recieptno },
        });
      } else {
        navigate("/frontshop/payment-types/cash", { state: { total, fromCOD: true, recieptno } });
      }
      return;
    }
    if (fromSplit) {
      navigate("/frontshop/payment-types/card", {
        state: { total, splitFlow: true, method1, fromDebtors: fromDebtorsSplit, paymentMethod: "Card", recieptno },
      });
      return;
    }
    navigate("/frontshop/payment-types/card", { state: { total, paymentMethod: "Card", fromCOD: true, recieptno } });
  };

  const handleAddItems = async () => {
    const rec = recieptno ? String(recieptno).trim() : "";
    if (!rec) return;
    try {
      await apiLoadUnpaidOrderToCart(rec);
    } catch {
      // ignore; landing will attempt to load again
    }
    navigate("/frontshop", {
      state: {
        editUnpaidOrder: true,
        editRecieptno: rec,
        returnTo: "/frontshop/payment-types/cod/payment",
        paymentTitle,
      },
    });
  };

  return (
    <div className={styles.wrapper}>
      <Navbar onBack={handleBack} />
      <main className={styles.main}>
        <div className={styles.card}>
          <header className={styles.header}>
            <h1 className={styles.title}>{paymentTitle}</h1>
            <p className={styles.subtitle}>Enter the amount received from customer</p>
          </header>
          <section className={styles.topRow}>
            <div className={styles.totalBox}>
              <div className={styles.totalLabel}>Total Due</div>
              <div className={styles.totalAmount}>R {total.toFixed(2)}</div>
            </div>
            <div className={styles.methodColumn}>
              <Button type="button" variant="primary" className={styles.addItemsBtn} onClick={handleAddItems}>
                <span className={styles.addItemsIcon}>
                  <LuShoppingCart />
                </span>
                Add Items
              </Button>
              <button
                type="button"
                className={`${styles.methodTile} ${selectedMethod === "cash" ? styles.methodActive : ""}`}
                onClick={() => setSelectedMethod("cash")}
              >
                <span className={styles.methodIcon}>
                  <LuBanknote />
                </span>
                Cash
              </button>
              <button
                type="button"
                className={`${styles.methodTile} ${selectedMethod === "card" ? styles.methodActive : ""}`}
                onClick={() => setSelectedMethod("card")}
              >
                <span className={styles.methodIcon}>
                  <FiCreditCard />
                </span>
                Card
              </button>
            </div>
          </section>
          <section className={styles.amountSection}>
            <div className={styles.amountLabel}>Amount Given</div>
            <input
              type="text"
              className={styles.amountBox}
              value={amountGiven}
              onChange={(e) => {
                const raw = e.target.value || "";
                const withoutPrefix = raw.replace(/^R\s*/i, "");
                setAmountGiven(`R ${withoutPrefix}`);
              }}
              placeholder="R 0.00"
            />
          </section>
          <div className={styles.buttonsRow}>
            <Button
              type="button"
              variant="approve"
              className={styles.continueBtn}
              onClick={handleContinue}
              disabled={!selectedMethod}
            >
              Continue to payment
            </Button>
          </div>
        </div>
      </main>
      <div className={styles.footerWrapper}>
        <Footer />
      </div>
    </div>
  );
}
